import React, { useState, useContext } from 'react';
import { useMutation } from '@apollo/react-hooks';

import './desktop-review-list-item-style.scss';
import { useSnackbar } from '@src/providers/SnackbarProvider';

import ThumbIcon from '@src/componentsNew/Icons/Icons/ThumbIcon';
import Typography from '@src/componentsNew/Typography/Typography';
import StarRatingIcon from '@src/componentsNew/Icons/StarRatingIcon';
import DesktopPhotoReviewDetailModal from '@src/componentsNew/Modals/PhotoReviewDetailModal/DesktopPhotoReviewDetailModal';

import { REVIEW_VOTE_CREATE } from '@molecules/ReviewList/queries';
import { reviewVoteCreate } from '@molecules/ReviewList/__generated__/reviewVoteCreate';
import { reviews_reviews_edges_node } from '@molecules/ReviewList/__generated__/reviews';

import { CredentialContext, credentialType } from '@providers';
import { getMaskedEmail } from '@src/core/utils';
import { formatDate } from '@src/utils';

interface IProps {
  review: reviews_reviews_edges_node;
  isPreview?: boolean;
  refetch?: any;
  onClick?: () => void;
}

const DesktopReviewListItem = ({ review, isPreview, refetch, onClick }: IProps) => {
  const { showSnackbar } = useSnackbar();
  const { currentUser } = useContext<credentialType>(CredentialContext);

  const [clickedImageIdx, setClickedImageIdx] = useState(0);
  const [isReviewDetailModalOpen, setIsReviewDetailModalOpen] = useState(false);

  const [createReviewVote] = useMutation<reviewVoteCreate>(REVIEW_VOTE_CREATE, {
    onCompleted: (data) => {
      const errors = data?.reviewVoteCreate?.errors;

      if (errors?.length === 0) {
        refetch();
      } else {
        showSnackbar({ message: errors[0].message });
      }
    },
  });

  const handleClickVote = (reviewId: string) => {
    if (currentUser?.token) {
      createReviewVote({
        variables: {
          input: {
            review: reviewId,
            voter: currentUser.user.id,
          },
        },
      });
    }
  };

  const handleReviewDetailModalClose = () => {
    setClickedImageIdx(0);
    setIsReviewDetailModalOpen(false);
  };

  const handleReviewDetailModalOpen = (idx: number) => {
    setClickedImageIdx(idx);
    setIsReviewDetailModalOpen(true);
  };

  return (
    <li className={`desktop-review-list-item ${isPreview ? 'preview' : ''}`} onClick={onClick}>
      {!isPreview && (
        <div className="review-list-item-rating-area non-preview">
          <div className="rating-wrapper">
            <StarRatingIcon width="20" height="20" />
            <Typography variant="subtitle2" component="span" style={{ color: '#212121' }}>
              {Number(review.rating).toFixed(1)}
            </Typography>
          </div>
          {review?.variant?.name ? (
            <Typography
              cName="review-list-item-vote-count"
              variant="caption"
              component="span"
              style={{ color: '#666666' }}
            >
              {review.variant.name}
            </Typography>
          ) : null}
        </div>
      )}

      <div className="review-content-wrapper">
        <div className="review-list-item-reviewer-area">
          <Typography cName="review-list-item-reviewer" variant="body3" component="span" style={{ color: '#11141D99' }}>
            {review.reviewerName || getMaskedEmail(review.reviewer.email)}
          </Typography>

          <div className="review-info-wrapper">
            <div className="vote-wrapper">
              <ThumbIcon onClick={() => handleClickVote(review.id)} />
              <Typography
                cName="review-list-item-vote-count"
                variant="caption"
                component="span"
                style={{ color: '#666666' }}
              >
                {`helpful (${review.totalVotes})`}
              </Typography>
            </div>

            <Typography
              cName="review-list-item-create-date"
              variant="caption"
              component="span"
              style={{ color: '#666666' }}
            >
              {formatDate(review.createdAt, '.')}
            </Typography>
          </div>
        </div>

        {isPreview && (
          <div className="review-list-item-rating-area">
            <div className="rating-wrapper">
              <StarRatingIcon />
              <Typography variant="subtitle2" component="span" style={{ color: '#212121' }}>
                {Number(review.rating).toFixed(1)}
              </Typography>
            </div>
            {review?.variant?.name ? (
              <Typography
                cName="review-list-item-vote-count"
                variant="caption"
                component="span"
                style={{ color: '#666666' }}
              >
                {review.variant.name}
              </Typography>
            ) : null}
          </div>
        )}

        {!isPreview && (
          <Typography cName="review-title" variant="subtitle2" component="span" style={{ color: '#212121' }}>
            {review.title}
          </Typography>
        )}

        <Typography
          cName={`review-list-item-comment-area ${isPreview ? 'preview' : ''}`}
          variant="body3"
          component="p"
          style={{ color: '#090A0FDE' }}
        >
          {review.comment}
        </Typography>

        {review.detailImages.length ? (
          <div className="review-list-item-image-area">
            {review.detailImages.map((image, idx) => (
              <img key={idx} src={image.url} onClick={() => handleReviewDetailModalOpen(idx)} />
            ))}
          </div>
        ) : null}
      </div>

      {isReviewDetailModalOpen && (
        <DesktopPhotoReviewDetailModal
          open={isReviewDetailModalOpen}
          onClose={handleReviewDetailModalClose}
          review={review}
          imageIdx={clickedImageIdx}
          onClickVoteButton={handleClickVote}
        />
      )}
    </li>
  );
};

export default DesktopReviewListItem;
